import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SERVICE_OPERATIONS_ERRORS, SERVICE_OPERATION_KEY_NAMES, SERVICE_VALIDATION_ERRORS, SERVICE_OPS_FORM_PARAM, SERVICE_OPERATION_MAX_TIME_OUT } from '../../constants';
import { TranslateHook } from '../../modules/schema-generator/Utils';
import { IServiceOperation, IServiceOperationValidationModel, InputElementModel } from '../../models/index';
import { useSelector } from '../../store/AppStore';

const serviceOperationName: InputElementModel = {
  name: 'serviceOperationName',
  value: '',
  error: SERVICE_VALIDATION_ERRORS.serviceOperationName,
  expression: /^[a-zA-Z]([a-zA-Z0-9-_])*$/,
  isValid: true,
  isTouched: false
};

const operationURL: InputElementModel = {
  name: 'operationURL',
  value: '',
  error: SERVICE_OPERATIONS_ERRORS.operationURL,
  expression: /^\/([!%#$&"'~()-{}])*$/i,
  isValid: true,
  isTouched: false
};

const retryCount: InputElementModel = {
  name: 'retryCount',
  value: '',
  error: SERVICE_OPERATIONS_ERRORS.retryCount,
  expression: /^\d*$/i,
  isValid: true,
  isTouched: false
};

const retryErrorCode: InputElementModel = {
  name: 'retryErrorCode',
  value: '',
  error: SERVICE_OPERATIONS_ERRORS.retryErrorCode,
  expression: /^(\d{3}(,\s*\d{3})*)?$/i,
  isValid: true,
  isTouched: false
};

const timeOut: InputElementModel = {
  name: 'timeOut',
  value: '',
  error: SERVICE_OPERATIONS_ERRORS.timeOut,
  expression: /^\d{1,5}$/i,
  isValid: true,
  isTouched: false
};
const requestAttributes: InputElementModel = {
  name: 'requestAttributes',
  value: '',
  expression: /^[^\s]+$/,
  error: SERVICE_OPERATIONS_ERRORS.reqAttInvalid,
  isValid: true,
  isTouched: false
};

const responseAttributes: InputElementModel = {
  name: 'responseAttributes',
  value: '',
  expression: /^[^\s]+$/,
  error: SERVICE_OPERATIONS_ERRORS.resAttInvalid,
  isValid: true,
  isTouched: false
};

const security: InputElementModel = {
  name: 'security',
  value: ''
};
const operationVerb: InputElementModel = {
  name: 'operationVerb',
  value: '',
};

const queryParamKey: InputElementModel = {
  name: 'queryParamKey',
  value: '',
  expression: /^([a-zA-Z0-9-_])*$/,
  error: SERVICE_OPERATIONS_ERRORS.invalidKey,
  isValid: true,
  isTouched: false
};

const queryParamValue: InputElementModel = {
  name: 'queryParamValue',
  value: '',
  expression: /^\S*$/,
  error: SERVICE_OPERATIONS_ERRORS.invalidValue,
  isValid: true,
  isTouched: false
};

const headerParamKey: InputElementModel = {
  name: 'headerParamKey',
  value: '',
  expression: /^([a-zA-Z0-9-_])*$/,
  error: SERVICE_OPERATIONS_ERRORS.invalidKey,
  isValid: true,
  isTouched: false
};

const headerParamValue: InputElementModel = {
  name: 'headerParamValue',
  value: '',
  expression: /^\S*$/,
  error: SERVICE_OPERATIONS_ERRORS.invalidValue,
  isValid: true,
  isTouched: false
};

const version: InputElementModel = {
  name: 'version',
  value: ''
};

export const serviceOpsFormParam: IServiceOperationValidationModel = {
  serviceOperationName,
  operationURL,
  retryErrorCode,
  retryCount,
  timeOut,
  requestAttributes,
  responseAttributes,
  security,
  operationVerb,
  queryParamKey,
  headerParamKey,
  queryParamValue,
  headerParamValue,
  version
};

/**
* @method getErrorName
* @description A method to gives error if invalid name is provided
* @returns evaluates and display error if any
*/
const getErrorName = (name: string, translate: TranslateHook): string => {
  let error;
  switch (name) {
    case SERVICE_OPS_FORM_PARAM.serviceOperationName:
      error = translate('service_validation_error.serviceOperationNameRequired');
      break;
    case SERVICE_OPS_FORM_PARAM.operationURL:
      error = translate('service_validation_error.serviceOpsURIRequired');
      break;
    // case SERVICE_OPS_FORM_PARAM.retryErrorCode:
    //   error = translate('service_validation_error.retryErrorCodeRequired');
    //   break;
    // case SERVICE_OPS_FORM_PARAM.retryCount:
    //   error = translate('service_validation_error.retryCountRequired');
    //   break;
    default:
      error = '';
  }
  return error;
};

/**
* @method getErrorName
* @description A method to gives error if invalid data is provided
* @returns evaluates and display error if any
*/
const getErrorForInvalidData = (name: string, translate: TranslateHook): string => {
  let error;
  switch (name) {
    case SERVICE_OPS_FORM_PARAM.serviceOperationName:
      error = translate('service_validation_error.serviceOperationName');
      break;
    case SERVICE_OPS_FORM_PARAM.operationURL:
      error = translate('service_validation_error.serviceOpsURI');
      break;
    case SERVICE_OPS_FORM_PARAM.retryErrorCode:
      error = translate('service_validation_error.retryErrorCodeInvalid');
      break;
    case SERVICE_OPS_FORM_PARAM.retryCount:
      error = translate('service_validation_error.retryCountInvalid');
      break;
    case SERVICE_OPS_FORM_PARAM.timeOut:
      error = translate('service_validation_error.timeOutInvalid');
      break;
    case SERVICE_OPS_FORM_PARAM.queryParamKey:
      error = translate('serviceOperationsError.invalidKey');
      break;
    case SERVICE_OPS_FORM_PARAM.queryParamValue:
      error = translate('serviceOperationsError.invalidValue');
      break;
    case SERVICE_OPS_FORM_PARAM.headerParamValue:
      error = translate('serviceOperationsError.invalidValue');
      break;
    case SERVICE_OPS_FORM_PARAM.headerParamKey:
      error = translate('serviceOperationsError.invalidKey');
      break;
    case SERVICE_OPS_FORM_PARAM.requestAttributes:
      error = translate('serviceOperationsError.request_attribute_invalid');
      break;
    case SERVICE_OPS_FORM_PARAM.responseAttributes:
        error = translate('serviceOperationsError.response_attribute_invalid');
        break;
    default:
      error = '';
  }
  return error;
};

/**
* @method handleInputChangeError
* @description A method to gives error on every key stroke if invalid input is provided
* @returns evaluates and display error if any
*/
const handleInputChangeError = (name: string, previousState: IServiceOperationValidationModel, translate: TranslateHook) => {
  const data = { ...previousState };
  const keyName = name as keyof IServiceOperationValidationModel;
  switch (name) {
    case SERVICE_OPS_FORM_PARAM.serviceOperationName:
      data[keyName].error = translate('service_validation_error.serviceOperationName');
      break;
    case SERVICE_OPS_FORM_PARAM.operationURL:
      data[keyName].error = translate('service_validation_error.serviceOpsURI');
      break;
    case SERVICE_OPS_FORM_PARAM.retryErrorCode:
      data[keyName].error = translate('service_validation_error.retryErrorCodeInvalid');
      break;
        case SERVICE_OPS_FORM_PARAM.retryCount:
      data[keyName].error = translate('service_validation_error.retryCountInvalid');
      break;
    case SERVICE_OPS_FORM_PARAM.timeOut:
      data[keyName].error = translate('service_validation_error.timeOutInvalid');
      break;
    case SERVICE_OPS_FORM_PARAM.queryParamKey:
      data[keyName].error = translate('serviceOperationsError.invalidKey');
      break;
    case SERVICE_OPS_FORM_PARAM.queryParamValue:
      data[keyName].error = translate('serviceOperationsError.invalidValue');
      break;
    case SERVICE_OPS_FORM_PARAM.headerParamValue:
      data[keyName].error = translate('serviceOperationsError.invalidValue');
      break;
    case SERVICE_OPS_FORM_PARAM.headerParamKey:
      data[keyName].error = translate('serviceOperationsError.invalidKey');
      break;
    case SERVICE_OPS_FORM_PARAM.requestAttributes:
        data[keyName].error = translate('serviceOperationsError.request_attribute_invalid');
        break;
    case SERVICE_OPS_FORM_PARAM.responseAttributes:
        data[keyName].error = translate('serviceOperationsError.response_attribute_invalid');
          break;
    default:
      data[keyName].error = '';
  }
};

/**
* @method checkValidityError
* @description checks the validation during import swagger
* @returns evaluates and display error if any
*/

const checkValidityError = (name: string, value: string, data: IServiceOperationValidationModel, translate: TranslateHook) => {
  const keyName = name as keyof IServiceOperationValidationModel;
  switch (name) {
    case SERVICE_OPERATION_KEY_NAMES.serviceOperationName:
      if (!value) data[keyName].error = translate('service_validation_error.serviceOperationNameRequired');
      else data[keyName].error = translate('service_validation_error.serviceOperationName');
      break;
    case SERVICE_OPERATION_KEY_NAMES.queryParamValue:
      if (!value) data[keyName].isValid = true;
      break;
    case SERVICE_OPERATION_KEY_NAMES.headerParamValue:
      if (!value) data[keyName].isValid = true;
      break;
    case SERVICE_OPERATION_KEY_NAMES.operationURL:
      if (!value) data[keyName].error = translate('service_validation_error.serviceOpsURIRequired');
      else data[keyName].error = translate('serviceOperationsError.operationURL');
      break;
    case SERVICE_OPERATION_KEY_NAMES.retryErrorCode:
      if (!value) data[keyName].error = '';
      else data[keyName].error = translate('serviceOperationsError.retryErrorCodeInvalid');
      break;
    case SERVICE_OPERATION_KEY_NAMES.retryCount:
      if (!value) data[keyName].error = '';
      else data[keyName].error = translate('serviceOperationsError.retryCountInvalid');
      break;
    case SERVICE_OPERATION_KEY_NAMES.timeOut:
      if (!value) data[keyName].error = '';
      else data[keyName].error = translate('service_validation_error.timeOutInvalid');
      break;
    case SERVICE_OPERATION_KEY_NAMES.requestAttributes:
       data[keyName].error = translate('serviceOperationsError.request_attribute_invalid');
        break;
    case SERVICE_OPERATION_KEY_NAMES.responseAttributes:
         data[keyName].error = translate('serviceOperationsError.response_attribute_invalid');
        break;
    default: data[keyName].error = '';
  }
};
const isExpressionExist = (
  name: string,
  serviceOpsFormData: IServiceOperationValidationModel
) => serviceOpsFormData[name as keyof IServiceOperationValidationModel] && serviceOpsFormData[name as keyof IServiceOperationValidationModel].expression;

const isValueExist = (
  name: string,
  serviceOpsFormData: IServiceOperationValidationModel
) => serviceOpsFormData[name as keyof IServiceOperationValidationModel] && serviceOpsFormData[name as keyof IServiceOperationValidationModel].value === '';

/**
 * Custom hook to handle Service Operation Form Inputs
 */
export const useServiceOpsForm = (serviceOperations: IServiceOperation[]) => {
  // Form values
  const [serviceOpsFormData, setFormParamValues] = useState(serviceOpsFormParam);
  const { t: translate } = useTranslation();
  const { serviceType } = useSelector((state) => state.serviceDetailsReducer);

  const checkServiceOperationExist = (value: string) => {
    const matchServiceOperation = serviceOperations?.filter((item) => item.serviceOperation === value);
    return !!matchServiceOperation?.length;
  };

  const recheckValidation = (serviceKeys: string[]) => {
    serviceKeys.forEach((key) => {
      const name = key as keyof IServiceOperationValidationModel;
      if (serviceOpsFormData[name]) {
        checkValidity(name, serviceOpsFormData[name].value, translate);
      }
    });
  };

  const checkValidity = (name: string, value: string, translate: TranslateHook) => {
    const keyName = name as keyof IServiceOperationValidationModel;
    const regExp = serviceOpsFormData[
      name as keyof IServiceOperationValidationModel
    ].expression as RegExp;
    const trimmedValue = value.trim();

    // Custom validation for optional timeout
    if (name === 'timeOut') {
      if (!trimmedValue) {
        setFormParamValues((previousState: IServiceOperationValidationModel) => {
          const data = { ...previousState };
          data[keyName].isValid = true;
          data[keyName].value = '';
          data[keyName].isTouched = true;
          data[keyName].error = '';
          return data;
        });
        return;
      }

      const timeoutValue = parseInt(trimmedValue, 10);
      if (timeoutValue > SERVICE_OPERATION_MAX_TIME_OUT) {
        setFormParamValues((previousState: IServiceOperationValidationModel) => {
          const data = { ...previousState };
          data[keyName].isValid = true;
          data[keyName].value = String(SERVICE_OPERATION_MAX_TIME_OUT); // set the value to the maximum allowed timeout value
          data[keyName].isTouched = true;
          data[keyName].error = '';
          return data;
        });
        return;
      }
    }

    if (
      value.length < 1
      || (serviceOpsFormData[name as keyof IServiceOperationValidationModel]
        && !regExp.test(value))
    ) {
      setFormParamValues((previousState: IServiceOperationValidationModel) => {
        const data = { ...previousState };
        data[keyName].isValid = false;
        data[keyName].value = value;
        data[keyName].isTouched = true;
        data[keyName].error = serviceOpsFormParam[name as keyof IServiceOperationValidationModel].error;
        checkValidityError(name, value, data, translate);
        return data;
      });
    } else if (name === 'serviceOperationName' && checkServiceOperationExist(value)) {
      // if Service Operation Name esxist show error
      setFormParamValues((previousState: IServiceOperationValidationModel) => {
        const data = structuredClone(previousState);
        data[keyName].isValid = false;
        data[keyName].value = value;
        data[keyName].isTouched = true;
        data[keyName].error = translate('serviceOperationsError.serviceOperationExist');
        return data;
      });
    } else {
      // set the error state empty or remove the error for service operation input
      setFormParamValues((previousState: IServiceOperationValidationModel) => {
        const data = { ...previousState };
        data[keyName].isValid = true;
        data[keyName].value = value;
        data[keyName].isTouched = true;
        return data;
      });
    }
    if (keyName === 'retryErrorCode' || keyName === 'retryCount') {
      if (serviceOpsFormData[name as keyof IServiceOperationValidationModel]
        && !regExp.test(value)) {
      setFormParamValues((previousState: IServiceOperationValidationModel) => {
        const data = { ...previousState };
        data[keyName].isValid = false;
        data[keyName].value = value;
        data[keyName].isTouched = true;
        data[keyName].error = serviceOpsFormParam[name as keyof IServiceOperationValidationModel].error;
        checkValidityError(name, value, data, translate);
        return data;
      });
      }
    }
  };

  // A method to handle form inputs
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const maxChars = event.target.maxLength > -1 ? event.target.maxLength : Number.MAX_SAFE_INTEGER;
    const currLength = event.target.value.length;

    if (isExpressionExist(name, serviceOpsFormData)) {
      checkValidity(name, value, translate);
    }

    if (name === 'timeOut') {
      const numeric = parseInt(value, 10);
      if (!Number.isNaN(numeric) && numeric > SERVICE_OPERATION_MAX_TIME_OUT) {
        return;
      }
    }
    // To check if length exceeds maximum length defined
    if (currLength <= maxChars) {
      setFormParamValues((previousState: IServiceOperationValidationModel) => {
        const keyName = name as keyof IServiceOperationValidationModel;
        const data = { ...previousState };
        if ((serviceType === 'DB2' || serviceType === 'MQ') && keyName === 'operationURL') {
          data[keyName].isValid = true;
          data[keyName].value = value;
          data[keyName].isTouched = true;
          data[keyName].error = '';
        } else if (currLength === maxChars) {
          data[keyName].isValid = false;
          data[keyName].value = value;
          data[keyName].isTouched = true;
          data[keyName].error = translate(
            'service_operation_form_validation_component.err_max_char_limit',
            { replace: { maxChars: maxChars - 1 } }
          );
        } else if (isValueExist(name, data)) {
          data[keyName].error = getErrorName(name, translate);
        } else {
          handleInputChangeError(name, data, translate);
          data[keyName].error = getErrorForInvalidData(name, translate);
        }
        return data;
      });
    }
  };
  return {
    handleInputChange,
    serviceOpsFormData,
    setFormParamValues,
    recheckValidation
  };
};
